import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedClip } from '../utils/clipApi';
import { visibleShows, Show } from '../shows';
import ViewersPanel from './viewers';
import '../components/silvr/silvr.scss';
import './board.scss';

interface Status {
  publishedAt?: string | null;
  tracks?: number;
  error?: string;
}

/** The board (dev.silvr.shop, localhost): every show as a tile with where it is live, when it was last
 *  published, and two ways in: Preview (the client page as production renders it) and Curate. */
const Board: React.FC = () => {
  const [status, setStatus] = useState<Record<string, Status>>({});

  useEffect(() => {
    let cancelled = false;
    visibleShows().forEach((show) => {
      getPublishedClip(show.slugs[0])
        .then((clip) => {
          if (!cancelled) setStatus((prev) => ({ ...prev, [show.key]: { publishedAt: clip.published_at, tracks: clip.tracks.length } }));
        })
        .catch((err: Error) => {
          if (!cancelled) setStatus((prev) => ({ ...prev, [show.key]: { error: err.message } }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const describe = (show: Show) => {
    const s = status[show.key];
    if (!s) return 'Checking…';
    if (s.error) return 'Not published yet';
    return `Published ${s.publishedAt ? new Date(s.publishedAt).toLocaleString() : ''} · ${s.tracks} garments`;
  };

  return (
    <main className="board silvr-ui">
      <header className="board__bar">
        <img src="/silvr-logo.png" alt="Silvr" className="h-6" />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Workshop</p>
          <h1 className="text-base font-semibold leading-tight">Shows</h1>
        </div>
      </header>
      <section className="board__grid">
        {visibleShows().map((show) => (
          <article key={show.key} className="board__tile">
            <div className="board__logo">
              <img src={show.logo} alt={show.logoAlt} />
            </div>
            <h2 className="text-lg font-semibold">{show.name}</h2>
            <p className="text-sm text-gray-600">
              Live at{' '}
              {show.host ? (
                <a href={`https://${show.host}/`} target="_blank" rel="noopener noreferrer" className="underline">
                  {show.host}
                </a>
              ) : show.localOnly ? (
                <span title="Listed on the local board only (localOnly); not on dev.silvr.shop or any production host">local only</span>
              ) : (
                <span title="Listed on the board (dev.silvr.shop and the local board); no production host">the board only</span>
              )}
            </p>
            <p className="text-xs text-gray-500">{describe(show)}</p>
            <p className="text-xs text-gray-400">
              {show.slugs.length} clip{show.slugs.length === 1 ? '' : 's'}: {show.slugs.join(', ')}
            </p>
            <div className="mt-4 flex gap-2">
              <Link to={`/${show.key}`} className="flex min-h-10 flex-1 items-center justify-center rounded-full border border-gray-200 px-4 text-sm font-medium">
                Preview
              </Link>
              {!show.curatedOn && (
                <Link to={`/${show.key}/curate`} className="flex min-h-10 flex-1 items-center justify-center rounded-full bg-gray-950 px-4 text-sm font-medium text-white">
                  Curate
                </Link>
              )}
            </div>
            {show.curatedOn && <p className="mt-2 text-xs text-gray-500">Same clips as /{show.curatedOn}: curate them there.</p>}
          </article>
        ))}
      </section>
      <ViewersPanel />
      <p className="px-6 pb-8 text-xs text-gray-500">
        Preview renders the client page exactly as the show's production host does. Curate and Viewers ask for the curation passcode. Shows are configured in{' '}
        <code>src/shows/index.ts</code>; a show with <code>signIn: 'email'</code> asks viewers for a work email on its own host, checked against the allowed domains above.
      </p>
    </main>
  );
};

export default Board;
